import { animate } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject
} from 'react';
import type { FilterDict } from '../../components/list-controls/types';
import { EMPTY_FILTERS } from './landingDocumentsDemoData';
import { mockPointerClick, sleep } from './landingMockPointerUtils';

export interface Point {
  x: number;
  y: number;
}

export type PopoverFacet = null | 'owner' | 'type' | 'lifecycle';

export type DemoTarget =
  | 'filterTrigger'
  | 'facetOwner'
  | 'facetType'
  | 'filterBack'
  | 'valueOwnerMe'
  | 'valueTypePrd'
  | 'chipTypeX'
  | 'chipOwnerX';

const TIMING_MS = {
  holdStart: 1200,
  move: 480,
  holdFiltered: 800,
  chipExit: 480,
  popoverClose: 250,
  pauseBeforeClick: 150,
  filterClick: 280,
  filterPaneSettle: 320,
  paneSettle: 180,
  layoutSettle: 140
} as const;

async function tweenPoint(
  from: Point,
  to: Point,
  durationMs: number,
  onUpdate: (p: Point) => void,
  cancelled: () => boolean
) {
  if (cancelled()) return;
  await new Promise<void>((resolve) => {
    const controls = animate(0, 1, {
      duration: durationMs / 1000,
      ease: [0.45, 0, 0.2, 1],
      onUpdate: (t) => {
        if (cancelled()) {
          controls.stop();
          return;
        }
        onUpdate({
          x: from.x + (to.x - from.x) * t,
          y: from.y + (to.y - from.y) * t
        });
      },
      onComplete: () => {
        if (!cancelled()) resolve();
      }
    });
  });
}

function centerOf(el: HTMLElement, stage: DOMRect): Point {
  const r = el.getBoundingClientRect();
  return {
    x: r.left + r.width / 2 - stage.left,
    y: r.top + r.height / 2 - stage.top
  };
}

/** Cursor hotspot sits up-left of the pointer box; bias from target center for approach tween. */
function approachPoint(el: HTMLElement, stage: DOMRect, bias = 0.32): Point {
  const r = el.getBoundingClientRect();
  const center = centerOf(el, stage);
  return {
    x: center.x + r.width * bias,
    y: center.y + r.height * bias
  };
}

export function useDocumentsFilterLoop(enabled: boolean) {
  const stageRef = useRef<HTMLDivElement>(null);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const facetOwnerRef = useRef<HTMLButtonElement>(null);
  const facetTypeRef = useRef<HTMLButtonElement>(null);
  const filterBackRef = useRef<HTMLButtonElement>(null);
  const valueOwnerMeRef = useRef<HTMLLabelElement>(null);
  const valueTypePrdRef = useRef<HTMLLabelElement>(null);
  const chipTypeXRef = useRef<HTMLButtonElement>(null);
  const chipOwnerXRef = useRef<HTMLButtonElement>(null);

  const targetRefs: Record<DemoTarget, RefObject<HTMLElement>> = {
    filterTrigger: filterTriggerRef,
    facetOwner: facetOwnerRef,
    facetType: facetTypeRef,
    filterBack: filterBackRef,
    valueOwnerMe: valueOwnerMeRef,
    valueTypePrd: valueTypePrdRef,
    chipTypeX: chipTypeXRef,
    chipOwnerX: chipOwnerXRef
  };

  const [filters, setFilters] = useState<FilterDict>({ ...EMPTY_FILTERS });
  const [pendingFilters, setPendingFilters] = useState<FilterDict>({ ...EMPTY_FILTERS });
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverFacet, setPopoverFacet] = useState<PopoverFacet>(null);
  const [showCursor, setShowCursor] = useState(false);
  const [pointerClicking, setPointerClicking] = useState(false);
  const [cursor, setCursor] = useState<Point>({ x: 0, y: 0 });
  const cursorRef = useRef<Point>({ x: 0, y: 0 });
  const [demoHighlight, setDemoHighlight] = useState<DemoTarget | null>(null);
  const [measureReady, setMeasureReady] = useState(false);

  const cancelledRef = useRef(false);

  const setCursorPoint = useCallback((p: Point) => {
    cursorRef.current = p;
    setCursor(p);
  }, []);

  const measureTarget = useCallback((target: DemoTarget): Point | null => {
    const stage = stageRef.current;
    const el = targetRefs[target].current;
    if (!stage || !el) return null;
    return centerOf(el, stage.getBoundingClientRect());
  }, []);

  const goTo = useCallback(
    async (target: DemoTarget, isCancelled: () => boolean) => {
      const pt = measureTarget(target);
      if (!pt) return;
      setDemoHighlight(null);
      await tweenPoint(cursorRef.current, pt, TIMING_MS.move, setCursorPoint, isCancelled);
      if (!isCancelled()) setDemoHighlight(target);
    },
    [measureTarget, setCursorPoint]
  );

  const click = useCallback(async (isCancelled: () => boolean, durationMs?: number) => {
    await mockPointerClick(setPointerClicking, isCancelled, durationMs);
  }, []);

  const filterGoAndClick = useCallback(
    async (target: DemoTarget, isCancelled: () => boolean) => {
      await goTo(target, isCancelled);
      if (isCancelled()) return;
      await sleep(TIMING_MS.pauseBeforeClick, isCancelled);
      if (isCancelled()) return;
      await click(isCancelled, TIMING_MS.filterClick);
    },
    [click, goTo]
  );

  const runLoop = useCallback(
    async (isCancelled: () => boolean) => {
      const reset = () => {
        setFilters({ ...EMPTY_FILTERS });
        setPendingFilters({ ...EMPTY_FILTERS });
        setPopoverOpen(false);
        setPopoverFacet(null);
        setDemoHighlight(null);
        setPointerClicking(false);
      };

      await sleep(TIMING_MS.holdStart, isCancelled);
      if (isCancelled()) return;

      while (!isCancelled()) {
        reset();
        await sleep(TIMING_MS.layoutSettle, isCancelled);
        if (isCancelled()) break;

        setShowCursor(true);
        const stage = stageRef.current;
        const trigger = filterTriggerRef.current;
        if (!stage || !trigger) break;
        setCursorPoint(approachPoint(trigger, stage.getBoundingClientRect()));
        await filterGoAndClick('filterTrigger', isCancelled);
        if (isCancelled()) break;
        setPopoverOpen(true);
        setPopoverFacet(null);
        await sleep(TIMING_MS.filterPaneSettle, isCancelled);

        await filterGoAndClick('facetOwner', isCancelled);
        if (isCancelled()) break;
        setPopoverFacet('owner');
        await sleep(TIMING_MS.filterPaneSettle, isCancelled);

        await filterGoAndClick('valueOwnerMe', isCancelled);
        if (isCancelled()) break;
        setPendingFilters({ ...EMPTY_FILTERS, owner: ['me'] });
        await sleep(TIMING_MS.filterPaneSettle, isCancelled);

        await filterGoAndClick('filterBack', isCancelled);
        if (isCancelled()) break;
        setPopoverFacet(null);
        setDemoHighlight(null);
        await sleep(TIMING_MS.filterPaneSettle, isCancelled);

        await filterGoAndClick('facetType', isCancelled);
        if (isCancelled()) break;
        setPopoverFacet('type');
        await sleep(TIMING_MS.filterPaneSettle, isCancelled);

        await filterGoAndClick('valueTypePrd', isCancelled);
        if (isCancelled()) break;
        setPendingFilters({ ...EMPTY_FILTERS, owner: ['me'], type: ['prd'] });
        await sleep(TIMING_MS.filterPaneSettle, isCancelled);

        setPopoverOpen(false);
        setPopoverFacet(null);
        setDemoHighlight(null);
        setFilters({ ...EMPTY_FILTERS, owner: ['me'], type: ['prd'] });
        await sleep(TIMING_MS.popoverClose + TIMING_MS.layoutSettle, isCancelled);
        if (isCancelled()) break;

        await sleep(TIMING_MS.holdFiltered, isCancelled);
        if (isCancelled()) break;

        await goTo('chipTypeX', isCancelled);
        if (isCancelled()) break;
        await sleep(TIMING_MS.pauseBeforeClick, isCancelled);
        if (isCancelled()) break;
        await click(isCancelled);
        setFilters({ ...EMPTY_FILTERS, owner: ['me'] });
        await sleep(TIMING_MS.chipExit + TIMING_MS.layoutSettle, isCancelled);
        if (isCancelled()) break;

        await sleep(TIMING_MS.holdFiltered / 2, isCancelled);
        if (isCancelled()) break;

        await goTo('chipOwnerX', isCancelled);
        if (isCancelled()) break;
        await sleep(TIMING_MS.pauseBeforeClick, isCancelled);
        if (isCancelled()) break;
        await click(isCancelled);
        setFilters({ ...EMPTY_FILTERS });
        await sleep(TIMING_MS.chipExit + TIMING_MS.layoutSettle, isCancelled);
        if (isCancelled()) break;

        setShowCursor(false);
        setDemoHighlight(null);
        await sleep(TIMING_MS.holdStart, isCancelled);
      }
    },
    [click, filterGoAndClick, measureTarget, setCursorPoint]
  );

  useLayoutEffect(() => {
    if (!enabled) {
      setMeasureReady(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      setMeasureReady(measureTarget('filterTrigger') !== null);
    });
    return () => cancelAnimationFrame(id);
  }, [enabled, popoverOpen, popoverFacet, filters, measureTarget]);

  useEffect(() => {
    if (!enabled || !measureReady) return;

    cancelledRef.current = false;
    const isCancelled = () => cancelledRef.current;

    void runLoop(isCancelled);

    return () => {
      cancelledRef.current = true;
    };
  }, [enabled, measureReady, runLoop]);

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setMeasureReady(measureTarget('filterTrigger') !== null);
      }, 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [enabled, measureTarget]);

  const popoverFilters = popoverOpen ? pendingFilters : filters;

  return {
    stageRef,
    filterTriggerRef,
    facetOwnerRef,
    facetTypeRef,
    filterBackRef,
    valueOwnerMeRef,
    valueTypePrdRef,
    chipTypeXRef,
    chipOwnerXRef,
    filters,
    popoverFilters,
    popoverOpen,
    popoverFacet,
    showCursor,
    pointerClicking,
    cursor,
    demoHighlight
  };
}
